import type { OmsOverviewResponse } from '../types';
import { computeOperationalHealth, deriveMomentum, deriveProjectPortfolio } from './deriveCommand';

/**
 * One-line executive briefing — the dashboard speaking to its owner.
 * Written like a world-class chief of staff: lead with the win, frame the
 * problem as the single next move, never scold, keep it short enough to
 * smile at. Deterministic per day (variant rotates daily, not per render).
 */
export function deriveBriefing(data: OmsOverviewResponse | null | undefined, now: Date = new Date()): string {
  const dayIndex = Math.floor(now.getTime() / 86_400_000);
  const pick = (variants: string[]): string => variants[dayIndex % variants.length] ?? variants[0] ?? '';

  const totals = data?.execution?.totals;
  if (!data || !totals) {
    return pick([
      'All quiet on the wire — the moment your field teams report in, I’ll brief you here.',
      'Fresh page today. As the data lands, you’ll hear it from me first.',
    ]);
  }

  const health = computeOperationalHealth(data);
  const momentum = deriveMomentum(data.trends.weekly ?? null);
  const portfolio = deriveProjectPortfolio(data.execution ?? null).filter((p) => p.totalTracked >= 3);
  const best = [...portfolio].sort((a, b) => b.passRate - a.passRate)[0];
  const riskiest = [...portfolio].sort((a, b) => b.riskShare - a.riskShare)[0];
  const overdue = (data.resources?.startedProjects ?? []).reduce((s, p) => s + p.overdueTasks, 0);
  const decided = totals.passTasks + totals.failTasks;
  const passRate = decided > 0 ? Math.round((totals.passTasks / decided) * 100) : null;

  if (health.score >= 80) {
    const variants = [
      passRate !== null
        ? `Portfolio in fine form — ${passRate}% pass rate. Leadership looks good on you.`
        : 'Portfolio in fine form. Leadership looks good on you.',
      best
        ? `${best.projectName} is setting the pace at ${best.passRate}% — and the fleet is following.`
        : 'Every site is pulling in the same direction today. Enjoy the view.',
      momentum.delta !== null && momentum.delta > 0
        ? `Up ${momentum.delta} pts this week. Momentum like this is earned, not lucky.`
        : 'Steady hands, green board. Some days the best move is to let a good team run.',
    ];
    return pick(variants);
  }

  if (health.score >= 55) {
    const variants = [
      riskiest && riskiest.risk !== 'low'
        ? `Mostly smooth sailing — ${riskiest.projectName} would love ten focused minutes from you today.`
        : 'Good shape overall — a light touch today keeps it that way.',
      overdue > 0
        ? `A solid week in the making. ${overdue} overdue task${overdue === 1 ? '' : 's'} — that’s the one knot worth untying first.`
        : 'The fundamentals are holding. One nudge where it’s amber and this board goes green.',
      momentum.delta !== null && momentum.delta > 0
        ? `Quietly climbing — +${momentum.delta} pts this week. Your kind of progress: no noise, all signal.`
        : 'Nothing here a good Tuesday can’t fix. You’ve turned tighter corners than this.',
    ];
    return pick(variants);
  }
 
  const variants = [
    overdue > 0
      ? `Storm day — and storms pass. Clearing ${overdue} overdue task${overdue === 1 ? '' : 's'} is the fastest route back to blue sky.`
      : 'A heavy board today. Pick one red, make it green — the rest tends to follow the leader.',
    riskiest
      ? `${riskiest.projectName} needs its captain today. Everything else can hold the line without you.`
      : 'Rough patch, right person for it. Small wins first — boards turn faster than they look.',
    'The numbers are loud today, but so is your track record. One focused hour changes this picture.',
  ];
  return pick(variants);
}
